import { useTheme } from "../hooks/use-theme";
import { LiftTheme } from "../theme";
import { Pressable, StyleProp, StyleSheet, TextStyle, View, ViewStyle } from "react-native";
import React, { useMemo } from "react";
import Text from "../components/Text";

export type LabelMode =
    | "default"
    | "warning"
    | "error"
    | "success"
    | "info"
    | "secondary"
    | "cta"
    | "redCta"
    | "miniCta"
    | "miniCtaBold"
    | "miniCtaGrey"
    | "anchor"
    | "medium"
    | "grey";

const LABEL_STYLES: Record<
    LabelMode,
    (theme: LiftTheme) => { containerStyle: ViewStyle; textStyle: TextStyle }
> = {
    cta: ({ colors, fonts }) => ({
        containerStyle: {
            backgroundColor: "transparent",
            borderColor: "transparent",
            paddingHorizontal: 0,
            paddingVertical: 0,
        },
        textStyle: {
            ...fonts.bodyMedium,
            color: colors.secondary,
        },
    }),
    redCta: ({ colors, fonts }) => ({
        containerStyle: {
            backgroundColor: "transparent",
            borderColor: "transparent",
            paddingHorizontal: 0,
            paddingVertical: 0,
        },
        textStyle: {
            ...fonts.bodyMedium,
            color: colors.notification,
        },
    }),
    miniCta: ({ colors, fonts }) => ({
        containerStyle: {
            backgroundColor: "transparent",
            borderColor: "transparent",
            paddingHorizontal: 0,
            paddingVertical: 0,
        },
        textStyle: {
            ...fonts.bodySmall,
            color: colors.secondary,
        },
    }),
    miniCtaBold: ({ colors, fonts }) => ({
        containerStyle: {
            backgroundColor: "transparent",
            borderColor: "transparent",
            paddingHorizontal: 0,
            paddingVertical: 0,
        },
        textStyle: {
            ...fonts.bodySmall,
            fontFamily: "Inter-Bold",
            color: colors.secondary,
        },
    }),
    miniCtaGrey: ({ colors, fonts }) => ({
        containerStyle: {
            backgroundColor: "transparent",
            borderColor: "transparent",
            paddingHorizontal: 0,
            paddingVertical: 0,
        },
        textStyle: {
            ...fonts.bodySmall,
            color: colors.greyAccessible,
        },
    }),
    anchor: ({ colors, fonts }) => ({
        containerStyle: {
            backgroundColor: "transparent",
            borderRadius: 0,
            paddingHorizontal: 0,
            paddingVertical: 0,
        },
        textStyle: {
            ...fonts.bodyMedium,
            color: colors.primary,
        },
    }),
    info: ({ colors }) => ({
        containerStyle: {
            backgroundColor: colors.infoBackground,
        },
        textStyle: {
            color: colors.onInfoBackground,
        },
    }),
    secondary: ({ colors }) => ({
        containerStyle: {
            backgroundColor: colors.onSecondaryContainer,
        },
        textStyle: {
            color: colors.secondary,
        },
    }),
    error: ({ colors }) => ({
        containerStyle: {
            backgroundColor: colors.errorBackground,
        },
        textStyle: {
            color: colors.onErrorBackground,
        },
    }),
    success: ({ colors }) => ({
        containerStyle: {
            backgroundColor: colors.successBackground,
        },
        textStyle: {
            color: colors.onSuccessBackground,
        },
    }),
    warning: ({ colors }) => ({
        containerStyle: {
            backgroundColor: colors.warningBackground,
        },
        textStyle: {
            color: colors.onWarningBackground,
        },
    }),
    default: ({ colors }) => ({
        containerStyle: {
            backgroundColor: colors.accentLighter,
        },
        textStyle: {
            color: "#626262",
        },
    }),
    medium: ({ colors, fonts }) => ({
        containerStyle: {
            backgroundColor: "transparent",
            borderColor: "transparent",
            borderWidth: 0,
            paddingHorizontal: 0,
        },
        textStyle: {
            ...fonts.bodyMedium,
            color: colors.text,
        },
    }),
    grey: ({ colors }) => ({
        containerStyle: {
            backgroundColor: colors.lighterGrey,
        },
        textStyle: {
            color: colors.darkGrey,
        },
    }),
};

export type LabelProps = {
    text?: string | number;
    mode?: LabelMode;
    style?: StyleProp<TextStyle>;
    multiline?: boolean;
    linkText?: string;
    onPress?: () => void;
};

const Label: React.FC<LabelProps> = ({
    text,
    mode = "default",
    style,
    multiline,
    linkText,
    onPress,
}) => {
    const styles = useStyles();
    const theme = useTheme();
    const { containerStyle, textStyle } = useMemo(() => LABEL_STYLES[mode](theme), [mode, theme]);
    return (
        <Pressable style={styles.container} onPress={onPress} disabled={!onPress}>
            <View style={[styles.labelContainer, containerStyle, style]}>
                {text && (
                    <Text
                        numberOfLines={multiline ? undefined : 1}
                        style={[styles.labelText, textStyle, style]}
                    >
                        {text}
                    </Text>
                )}
                {linkText ? (
                    <Text style={[styles.labelText, styles.linkText, textStyle]}>{linkText} ➜</Text>
                ) : null}
            </View>
            {mode == "anchor" && <View style={styles.underline} />}
        </Pressable>
    );
};

const useStyles = () => {
    const { gridSize, fonts, colors } = useTheme();
    return useMemo(
        () =>
            StyleSheet.create({
                container: {
                    flexDirection: "column",
                    // Let the label give way when its row is too narrow, so its text wraps (or
                    // ellipsizes when not `multiline`) rather than overflowing: React Native
                    // defaults flexShrink to 0, which pins the label to its intrinsic width.
                    flexShrink: 1,
                },
                labelContainer: {
                    position: "relative",
                    justifyContent: "center",
                    verticalAlign: "middle",
                    borderRadius: 4,
                    overflow: "hidden",
                    paddingHorizontal: gridSize / 4,
                    paddingVertical: gridSize / 8,
                    alignItems: "center",
                },
                labelText: {
                    ...fonts.labelSmall,
                },
                linkText: {
                    fontFamily: "Inter-Bold",
                },
                underline: {
                    position: "absolute",
                    bottom: 0,
                    left: 0,
                    right: 0,
                    height: 1,
                    backgroundColor: colors.primary,
                    zIndex: 2,
                },
            }),
        [fonts.labelSmall, gridSize, colors],
    );
};

export default Label;
